/* eslint-disable no-console */
import { useCallback } from "react";
import { useCallingStore } from "../../store";
import { useAuth } from "@tellescope/tanstack-query";

/**
 * Hook for conference call and warm transfer operations.
 *
 * Responsibilities:
 * - Upgrade standard call to conference
 * - Add attendees (by user ID or phone number)
 * - Remove attendees
 * - End conference
 * - Warm transfer (consult-then-transfer)
 */
export function useConferenceOperations() {
  const { session } = useAuth();
  const callingStore = useCallingStore();

  // Get store actions directly to avoid stale closures
  const getCallingActions = useCallback(() => {
    return {
      setConference: callingStore.setConference,
      updateConferenceAttendees: callingStore.updateConferenceAttendees,
      setWarmTransfer: callingStore.setWarmTransfer,
      clearWarmTransfer: callingStore.clearWarmTransfer,
      resetCallState: callingStore.resetCallState,
    };
  }, [
    callingStore.clearWarmTransfer,
    callingStore.resetCallState,
    callingStore.setConference,
    callingStore.setWarmTransfer,
    callingStore.updateConferenceAttendees,
  ]);

  /**
   * Upgrades the current call to a conference call
   */
  const upgradeToConference = useCallback(async (): Promise<boolean> => {
    const activeCall = callingStore.activeCall;
    if (!activeCall || !session) {
      console.error("[Calling:Conference] No active call or session");
      return false;
    }

    const callSid = activeCall.parameters?.CallSid;
    if (!callSid) {
      console.error("[Calling:Conference] No CallSid available");
      return false;
    }

    try {
      await session.api.phone_calls.upgrade_to_conference({ id: callSid });

      const actions = getCallingActions();
      const activeCallMeta = callingStore.activeCallMeta;

      actions.setConference({
        id: callSid,
        users: [],
        phones: [],
        enduserName: activeCallMeta.enduserName ?? "",
      });

      console.log("[Calling:Conference] Upgraded to conference");
      return true;
    } catch (error) {
      console.error("[Calling:Conference] Failed to upgrade to conference:", error);
      return false;
    }
  }, [callingStore.activeCall, callingStore.activeCallMeta, getCallingActions, session]);

  /**
   * Adds a user to the conference by their user ID
   */
  const addAttendeeByUser = useCallback(
    async (userId: string, userTwilioNumber?: string): Promise<boolean> => {
      const conference = callingStore.conference;
      const activeCallMeta = callingStore.activeCallMeta;

      if (!conference || !session) {
        console.error("[Calling:Conference] No conference or session");
        return false;
      }

      // Determine the Twilio number to use for the user
      const orgTwilioNumber = session.userInfo?.orgTwilioNumber;
      const twilioNumber = userTwilioNumber || orgTwilioNumber;

      if (!twilioNumber) {
        console.error("[Calling:Conference] No Twilio number available");
        return false;
      }

      try {
        // Format: twilioNumber-userId-businessId
        const byClientId = [`${twilioNumber}-${userId}-${session.userInfo?.businessId}`];

        await session.api.phone_calls.add_conference_attendees({
          conferenceId: conference.id,
          enduserId: activeCallMeta.enduserId ?? undefined,
          byClientId,
        });

        const actions = getCallingActions();
        actions.updateConferenceAttendees([...conference.users, userId], conference.phones);

        console.log("[Calling:Conference] Added user to conference:", userId);
        return true;
      } catch (error) {
        console.error("[Calling:Conference] Failed to add user:", error);
        return false;
      }
    },
    [callingStore.activeCallMeta, callingStore.conference, getCallingActions, session]
  );

  /**
   * Adds a phone number to the conference
   */
  const addAttendeeByPhone = useCallback(
    async (phoneNumber: string): Promise<boolean> => {
      const conference = callingStore.conference;
      const activeCallMeta = callingStore.activeCallMeta;

      if (!conference || !session) {
        console.error("[Calling:Conference] No conference or session");
        return false;
      }

      try {
        await session.api.phone_calls.add_conference_attendees({
          conferenceId: conference.id,
          enduserId: activeCallMeta.enduserId ?? undefined,
          byPhone: [phoneNumber],
        });

        const actions = getCallingActions();
        actions.updateConferenceAttendees(conference.users, [...conference.phones, phoneNumber]);

        console.log("[Calling:Conference] Added phone to conference");
        return true;
      } catch (error) {
        console.error("[Calling:Conference] Failed to add phone:", error);
        return false;
      }
    },
    [callingStore.activeCallMeta, callingStore.conference, getCallingActions, session]
  );

  /**
   * Removes an attendee from the conference
   */
  const removeAttendee = useCallback(
    async (attendeeId: string, isPhone: boolean = false): Promise<boolean> => {
      const conference = callingStore.conference;

      if (!conference || !session) {
        console.error("[Calling:Conference] No conference or session");
        return false;
      }

      try {
        if (isPhone) {
          await session.api.phone_calls.remove_conference_attendees({
            conferenceId: conference.id,
            byPhone: [attendeeId],
          });

          const actions = getCallingActions();
          actions.updateConferenceAttendees(
            conference.users,
            conference.phones.filter((p) => p !== attendeeId)
          );
        } else {
          const orgTwilioNumber = session.userInfo?.orgTwilioNumber;
          const byClientId = [`${orgTwilioNumber}-${attendeeId}-${session.userInfo?.businessId}`];

          await session.api.phone_calls.remove_conference_attendees({
            conferenceId: conference.id,
            byClientId,
          });

          const actions = getCallingActions();
          actions.updateConferenceAttendees(
            conference.users.filter((u) => u !== attendeeId),
            conference.phones
          );
        }

        console.log("[Calling:Conference] Removed attendee:", attendeeId);
        return true;
      } catch (error) {
        console.error("[Calling:Conference] Failed to remove attendee:", error);
        return false;
      }
    },
    [callingStore.conference, getCallingActions, session]
  );

  /**
   * Ends the conference call for all participants
   */
  const endConference = useCallback(async (): Promise<boolean> => {
    const conference = callingStore.conference;
    const device = callingStore.device;

    if (!conference || !session) {
      console.error("[Calling:Conference] No conference or session");
      return false;
    }

    try {
      await session.api.phone_calls.end_conference({ id: conference.id });

      // Disconnect local call
      device?.disconnectAll();

      const actions = getCallingActions();
      actions.resetCallState();

      console.log("[Calling:Conference] Conference ended");
      return true;
    } catch (error) {
      console.error("[Calling:Conference] Failed to end conference:", error);
      return false;
    }
  }, [callingStore.conference, callingStore.device, getCallingActions, session]);

  // ===== WARM TRANSFER OPERATIONS =====

  /**
   * Initiates a warm transfer to another user.
   *
   * Flow:
   * 1. Upgrade to conference (if not already)
   * 2. Add transfer target to conference
   */
  const initiateWarmTransfer = useCallback(
    async (targetUserId: string, targetUserName?: string, targetTwilioNumber?: string): Promise<boolean> => {
      const conference = callingStore.conference;
      const actions = getCallingActions();

      // If not already a conference, upgrade first
      if (!conference) {
        const upgraded = await upgradeToConference();
        if (!upgraded) {
          console.error("[Calling:Conference] Failed to upgrade for warm transfer");
          return false;
        }
      }

      // Add the transfer target
      const added = await addAttendeeByUser(targetUserId, targetTwilioNumber);
      if (!added) {
        console.error("[Calling:Conference] Failed to add transfer target");
        return false;
      }

      // Set warm transfer state
      actions.setWarmTransfer({
        isActive: true,
        targetUserId,
        targetUserName: targetUserName ?? null,
      });

      console.log("[Calling:Conference] Warm transfer initiated to:", targetUserId);
      return true;
    },
    [addAttendeeByUser, callingStore.conference, getCallingActions, upgradeToConference]
  );

  /**
   * Completes the warm transfer - current agent leaves the conference.
   * The transfer target and original caller remain connected.
   */
  const completeWarmTransfer = useCallback(async (): Promise<boolean> => {
    const warmTransfer = callingStore.warmTransfer;
    const device = callingStore.device;

    if (!warmTransfer.isActive) {
      console.error("[Calling:Conference] No warm transfer in progress");
      return false;
    }

    try {
      // Simply disconnect - the conference continues with other participants
      device?.disconnectAll();

      const actions = getCallingActions();
      actions.clearWarmTransfer();
      actions.resetCallState();

      console.log("[Calling:Conference] Warm transfer completed");
      return true;
    } catch (error) {
      console.error("[Calling:Conference] Failed to complete warm transfer:", error);
      return false;
    }
  }, [callingStore.device, callingStore.warmTransfer, getCallingActions]);

  /**
   * Cancels the warm transfer - removes the transfer target from conference.
   * Agent returns to the original call with the caller.
   */
  const cancelWarmTransfer = useCallback(async (): Promise<boolean> => {
    const warmTransfer = callingStore.warmTransfer;

    if (!warmTransfer.isActive || !warmTransfer.targetUserId) {
      console.error("[Calling:Conference] No warm transfer to cancel");
      return false;
    }

    // Remove the transfer target from the conference
    const removed = await removeAttendee(warmTransfer.targetUserId, false);
    if (!removed) {
      console.error("[Calling:Conference] Failed to remove transfer target");
      return false;
    }

    const actions = getCallingActions();
    actions.clearWarmTransfer();

    console.log("[Calling:Conference] Warm transfer cancelled");
    return true;
  }, [callingStore.warmTransfer, getCallingActions, removeAttendee]);

  return {
    // State
    conference: callingStore.conference,
    warmTransfer: callingStore.warmTransfer,
    isInConference: callingStore.conference !== null,
    isWarmTransferActive: callingStore.warmTransfer.isActive,

    // Conference operations
    upgradeToConference,
    addAttendeeByUser,
    addAttendeeByPhone,
    removeAttendee,
    endConference,

    // Warm transfer operations
    initiateWarmTransfer,
    completeWarmTransfer,
    cancelWarmTransfer,
  };
}
